/*
 * Kitty Up — Accessibility Phase 1 (additive progressive enhancement)
 * Scope: cart drawer dialog semantics + focus mgmt + announcements,
 *        cart line-item control names, header SHOP/About dropdown semantics.
 * Non-destructive: only adds ARIA / focus behavior. No visual or layout change.
 * Safe to remove by deleting this file + its <script> include.
 */
(function () {
  'use strict';
  if (window.__kittyA11yPhase1) return;
  window.__kittyA11yPhase1 = true;

  /* ---------- shared polite live region ---------- */
  var liveRegion = null;
  function ensureLive() {
    if (liveRegion && document.body.contains(liveRegion)) return liveRegion;
    liveRegion = document.createElement('div');
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.setAttribute('role', 'status');
    liveRegion.className = 'kitty-a11y-live';
    liveRegion.style.cssText =
      'position:absolute!important;width:1px;height:1px;margin:-1px;padding:0;' +
      'overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0;';
    document.body.appendChild(liveRegion);
    return liveRegion;
  }
  function announce(msg) {
    if (!msg) return;
    var l = ensureLive();
    l.textContent = '';
    window.setTimeout(function () { l.textContent = msg; }, 40);
  }

  /* ---------- helpers ---------- */
  function focusables(container) {
    var sel = 'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),' +
      'select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
    return Array.prototype.filter.call(container.querySelectorAll(sel), function (el) {
      return el.offsetParent !== null || el.getClientRects().length > 0;
    });
  }

  /* =========================================================
   * 1) CART DRAWER
   * ========================================================= */
  var GENERIC_LABELS = {
    'button-remove': 'Remove item',
    'button-minus': 'Decrease quantity',
    'button-plus': 'Increase quantity'
  };

  function productNameForItem(item) {
    var q = item.querySelector('input[aria-label^="Quantity for "]');
    if (q) return q.getAttribute('aria-label').replace(/^Quantity for\s*/i, '').trim();
    var a = item.querySelector('a[href*="/products/"]');
    if (a) {
      var t = (a.textContent || '').replace(/\s+/g, ' ').trim();
      if (t) return t;
    }
    return '';
  }

  function relabelCartControls(root) {
    if (!root) return;
    var items = root.querySelectorAll('[id^="CartItem-"], .cart-item');
    Array.prototype.forEach.call(items, function (item) {
      var name = productNameForItem(item);
      var forName = name ? (' of ' + name) : '';
      var rem = item.querySelector('[aria-label="button-remove"]');
      var minus = item.querySelector('[aria-label="button-minus"]');
      var plus = item.querySelector('[aria-label="button-plus"]');
      if (rem) rem.setAttribute('aria-label', 'Remove ' + (name || 'item'));
      if (minus) minus.setAttribute('aria-label', 'Decrease quantity' + forName);
      if (plus) plus.setAttribute('aria-label', 'Increase quantity' + forName);
    });
    /* any placeholder labels not inside a line item (e.g. upsell add) -> generic */
    var leftovers = root.querySelectorAll(
      '[aria-label="button-remove"],[aria-label="button-minus"],[aria-label="button-plus"]');
    Array.prototype.forEach.call(leftovers, function (el) {
      var g = GENERIC_LABELS[el.getAttribute('aria-label')];
      if (g) el.setAttribute('aria-label', g);
    });
  }

  var drawer = null;
  var drawerTrigger = null;
  var lastFocusBeforeOpen = null;
  var drawerWasOpen = false;
  var inertedEls = [];

  /* The theme toggles the `cart-open` class on #CartDrawer as its authoritative
   * open/close signal. Key off that only — a computed-style/display fallback is
   * unreliable across the open/close transition and would strand focus + inert. */
  function drawerIsOpen(d) {
    if (!d) return false;
    return /(^|\s)cart-open(\s|$)/.test(d.className);
  }

  /* The theme renders MULTIPLE cart triggers (desktop + mobile) that all carry
   * aria-controls="CartDrawer"; only one is visible per viewport. Focus can only
   * be restored to a visible trigger, so pick the first rendered/visible match.
   * The hidden duplicate would silently swallow .focus() and strand focus on body. */
  function allCartTriggers() {
    return Array.prototype.slice.call(
      document.querySelectorAll('[aria-controls="CartDrawer"]'));
  }
  function visibleCartTrigger() {
    var all = allCartTriggers();
    for (var i = 0; i < all.length; i++) {
      if (all[i].offsetParent !== null || all[i].getClientRects().length > 0) return all[i];
    }
    return all[0] || null;
  }
  function setTriggersExpanded(state) {
    allCartTriggers().forEach(function (t) { t.setAttribute('aria-expanded', state); });
  }

  /* Elements that must NOT be inerted while the drawer is open:
   * scripts/styles, the live region, Shopify system/preview UI, and the
   * drawer's own dim/backdrop overlay (which must stay click-through to close). */
  function isSkippableInert(el) {
    var tag = el.tagName;
    if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'TEMPLATE' || tag === 'LINK') return true;
    if (el === liveRegion) return true;
    if (tag.indexOf('SHOPIFY-') === 0) return true; /* <shopify-chat> etc. */
    var id = (el.id || '').toLowerCase();
    if (id.indexOf('web-pixels') !== -1) return true;
    if (id.indexOf('pbar') !== -1) return true;              /* theme preview bar */
    if (id.indexOf('shopify-block-') === 0) return true;
    if (id.indexOf('shopify-preview') !== -1 || id.indexOf('preview-bar') !== -1) return true;
    if (id === 'x-page-transition') return true;
    var cls = (el.className && el.className.toString ? el.className.toString() : '').toLowerCase();
    if (cls.indexOf('shopify-pc__') !== -1) return true;
    /* drawer dim/backdrop overlay — leave interactive so backdrop-click closes cart */
    var pos = '';
    try { pos = window.getComputedStyle(el).position; } catch (e) { /* noop */ }
    if (pos === 'fixed' && (cls.indexOf('bg-[rgba(0,0,0') !== -1 ||
        cls.indexOf('overlay') !== -1 || cls.indexOf('backdrop') !== -1)) return true;
    return false;
  }

  /* Inert everything outside the drawer by walking the drawer's ancestor
   * chain and inerting each ancestor's *siblings*. The cart drawer shares an
   * ancestor (.main-container) with the page content, so body-level inerting
   * alone would miss the nav/main/footer — this covers them correctly. */
  function setBackgroundInert(on) {
    if (on) {
      if (inertedEls.length) return;
      var node = drawer;
      while (node && node !== document.body && node.parentElement) {
        var parent = node.parentElement;
        var sibs = parent.children;
        for (var i = 0; i < sibs.length; i++) {
          var el = sibs[i];
          if (el === node) continue;
          if (drawer && el.contains(drawer)) continue;
          if (isSkippableInert(el)) continue;
          if (el.hasAttribute('data-kitty-inert')) continue;
          try {
            el.setAttribute('data-kitty-inert', '1');
            if ('inert' in el) el.inert = true;
            el.setAttribute('aria-hidden', 'true');
            inertedEls.push(el);
          } catch (e) { /* noop */ }
        }
        node = parent;
      }
    } else {
      inertedEls.forEach(function (el) {
        try {
          if ('inert' in el) el.inert = false;
          el.removeAttribute('aria-hidden');
          el.removeAttribute('data-kitty-inert');
        } catch (e) { /* noop */ }
      });
      inertedEls = [];
    }
  }

  function drawerKeydown(e) {
    if (!drawerIsOpen(drawer)) return;
    if (e.key === 'Escape' || e.key === 'Esc') {
      var closeBtn = drawer.querySelector('[aria-label="Close cart"]');
      if (closeBtn) { e.preventDefault(); closeBtn.click(); }
      return;
    }
    if (e.key === 'Tab') {
      var f = focusables(drawer);
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      } else if (!drawer.contains(document.activeElement)) {
        e.preventDefault(); first.focus();
      }
    }
  }

  function setupDrawer(d) {
    if (!d || d.__kittyReady) { if (d) { /* re-check open */ } }
    drawer = d;
    if (!d.getAttribute('role')) d.setAttribute('role', 'dialog');
    d.setAttribute('aria-modal', 'true');
    if (!d.getAttribute('aria-label') && !d.getAttribute('aria-labelledby')) {
      d.setAttribute('aria-label', 'Shopping cart');
    }
    if (!d.hasAttribute('tabindex')) d.setAttribute('tabindex', '-1');
    if (!d.__kittyReady) {
      d.addEventListener('keydown', drawerKeydown, true);
      /* re-apply labels whenever the drawer contents re-render (qty change / remove) */
      var mo = new MutationObserver(function () {
        relabelCartControls(d);
        handleDrawerState();
      });
      mo.observe(d, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
      d.__kittyReady = true;
    }
    relabelCartControls(d);
  }

  function handleDrawerState() {
    if (!drawer) return;
    var open = drawerIsOpen(drawer);
    if (open && !drawerWasOpen) {
      drawerWasOpen = true;
      drawerTrigger = visibleCartTrigger();
      lastFocusBeforeOpen = drawerTrigger || document.activeElement;
      setTriggersExpanded('true');
      relabelCartControls(drawer);
      setBackgroundInert(true);
      window.setTimeout(function () {
        var closeBtn = drawer.querySelector('[aria-label="Close cart"]');
        var target = closeBtn || focusables(drawer)[0] || drawer;
        try { target.focus(); } catch (e) { /* noop */ }
      }, 80);
      announce('Cart drawer opened');
    } else if (!open && drawerWasOpen) {
      drawerWasOpen = false;
      setTriggersExpanded('false');
      setBackgroundInert(false);
      /* Restore focus to the trigger. If the recorded one is now hidden (viewport
       * changed) fall back to whichever trigger is currently visible. */
      var restore = lastFocusBeforeOpen;
      if (!restore || restore.offsetParent === null) restore = visibleCartTrigger();
      if (restore && document.contains(restore)) {
        try { restore.focus(); } catch (e) { /* noop */ }
      }
    }
  }

  /* watch for the drawer being injected into the DOM, then wire it up */
  function watchForDrawer() {
    var existing = document.getElementById('CartDrawer');
    if (existing) setupDrawer(existing);
    var mo = new MutationObserver(function () {
      var d = document.getElementById('CartDrawer');
      if (d && d !== drawer) setupDrawer(d);
      else if (d && !d.__kittyReady) setupDrawer(d);
      if (drawer) handleDrawerState();
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  /* announce cart count changes from the trigger's accessible label */
  function watchCartCount() {
    var trigger = visibleCartTrigger();
    if (!trigger) return;
    var read = function () {
      var m = (trigger.getAttribute('aria-label') || '').match(/(\d+)\s*item/i);
      return m ? parseInt(m[1], 10) : null;
    };
    var prev = read();
    var mo = new MutationObserver(function () {
      var now = read();
      if (now !== null && prev !== null && now !== prev) {
        announce(now === 1 ? '1 item in cart' : (now + ' items in cart'));
      }
      if (now !== null) prev = now;
    });
    mo.observe(trigger, { attributes: true, attributeFilter: ['aria-label'], subtree: false });
  }

  /* =========================================================
   * 2) HEADER SHOP / About DROPDOWNS
   * ========================================================= */
  function primaryTrigger(li) {
    var links = li.querySelectorAll('a');
    for (var i = 0; i < links.length; i++) {
      if (links[i].getAttribute('tabindex') !== '-1') return links[i];
    }
    return links[0] || null;
  }

  function setupDropdown(li, idx) {
    if (li.__kittyDd) return;
    var trigger = primaryTrigger(li);
    if (!trigger) return;
    li.__kittyDd = true;
    trigger.setAttribute('aria-haspopup', 'true');

    var syncControls = function () {
      var panel = li.querySelector('.toggle-menu');
      if (panel) {
        if (!panel.id) panel.id = 'kitty-submenu-' + idx;
        trigger.setAttribute('aria-controls', panel.id);
      }
    };
    var syncExpanded = function () {
      trigger.setAttribute('aria-expanded', li.classList.contains('is-focus') ? 'true' : 'false');
      syncControls();
    };
    syncExpanded();
    new MutationObserver(syncExpanded).observe(li, {
      attributes: true, attributeFilter: ['class'], childList: true, subtree: false
    });

    li.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        li.classList.remove('is-focus');
        li.dispatchEvent(new MouseEvent('mouseleave', { bubbles: true }));
        if (document.activeElement && li.contains(document.activeElement)) {
          try { trigger.focus(); } catch (err) { /* noop */ }
        }
        syncExpanded();
      }
    });
  }

  function setupDropdowns() {
    var lis = document.querySelectorAll('li.has-dropdown');
    Array.prototype.forEach.call(lis, function (li, i) { setupDropdown(li, i); });
  }

  /* =========================================================
   * INIT
   * ========================================================= */
  function init() {
    ensureLive();
    watchForDrawer();
    watchCartCount();
    setupDropdowns();
    /* header markup can hydrate late (Alpine) — re-scan a few times */
    var tries = 0;
    var iv = window.setInterval(function () {
      setupDropdowns();
      if (++tries >= 6) window.clearInterval(iv);
    }, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
